import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { API_ENDPOINTS } from "../constants/repositoryConstants";

/**
 * Hook for fetching and managing repository data
 * @param {Object} options - Hook options
 * @param {string} options.reportName - The report name to fetch data for
 * @param {boolean} options.autoFetch - Whether to auto-fetch when reportName changes (default: true)
 * @returns {Object} { data, linkData, loading, error, refresh }
 */
const useRepositoryData = ({ reportName = null, autoFetch = true } = {}) => {
  const [data, setData] = useState([]);
  const [linkData, setLinkData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchedReportName = useRef(null);
  const pollAttemptsRef = useRef(0);
  const prevHasProcessingRef = useRef(false);

  const getEffectiveReportName = useCallback(() => {
    // If reportName is a non-empty string, use it
    if (typeof reportName === "string" && reportName.trim() !== "") {
      return reportName;
    }
    // If reportName is an empty string, wait for it to be populated (return null)
    if (reportName === "") {
      return null;
    }
    // If reportName is null/undefined, fall back to localStorage
    const storedReportName = localStorage.getItem("report_name");
    const effectiveName = storedReportName && storedReportName !== "undefined" ? storedReportName : "Repository";
    if (!storedReportName || storedReportName === "undefined") {
      localStorage.setItem("report_name", effectiveName);
    }
    return effectiveName;
  }, [reportName]);

  const fetchData = useCallback(async (forceReportName = null) => {
    const effectiveReportName = forceReportName || getEffectiveReportName();
    const companyid = localStorage.getItem("companyid");
    const username = localStorage.getItem("userName");
    const organization = localStorage.getItem("organization");

    // Skip fetch if no valid report name
    if (!effectiveReportName || effectiveReportName.trim() === "") {
      return;
    }

    setLoading(true);
    setError(null);
    lastFetchedReportName.current = effectiveReportName;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.LIST_REPOSITORY}`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            username: username,
          },
          body: JSON.stringify({
            company_id: companyid,
            report_name: effectiveReportName,
          }),
        }
      );

      if (response.status === 200) {
        const responseJson = await response.json();

        if (responseJson.success === false || responseJson.status === 400) {
          setData([]);
          setLinkData([]);
        } else if (responseJson.detail) {
          setData([]);
          setLinkData([]);
        } else {
          const transformedData = Object.keys(responseJson.training_status).map((key) => {
            const item = responseJson.training_status[key];
            return {
              Name: key,
              Category: item.category || "",
              Username: organization,
              DateTime: item.upload_datetime,
              Link: item.link || item.url || item.file_link || item.file_url || "",
              blob_upload_status: item.blob_upload_status,
              Status:
                item.vector_upload_status === "completed" || item.vector_upload_status === "complete"
                  ? "Analysis complete"
                  : item.vector_upload_status === "error" || item.vector_upload_status === "failed"
                  ? "Error"
                  : item.vector_upload_status
                  ? "Analyzing file"
                  : "Pending",
              backgroundColor:
                item.vector_upload_status === "completed" || item.vector_upload_status === "complete"
                  ? "#407775"
                  : item.vector_upload_status === "error" || item.vector_upload_status === "failed"
                  ? "#ffebee"
                  : "#ffe4e4",
              vector_upload_time: item.vector_upload_time,
            };
          });
          setData(transformedData);

          // Populate links section from registry returned by backend
          if (Array.isArray(responseJson.links)) {
            setLinkData(responseJson.links);
          }
        }
      } else {
        setData([]);
        setError(`Failed to load documents (Status: ${response.status})`);
      }
    } catch (err) {
      console.error(err);
      setError("Unable to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [getEffectiveReportName]);

  // Auto-fetch when reportName changes (and is valid)
  useEffect(() => {
    if (!autoFetch) return;

    const effectiveReportName = getEffectiveReportName();

    // Only fetch if we have a valid report name and it's different from last fetch
    if (effectiveReportName && effectiveReportName.trim() !== "" &&
        effectiveReportName !== lastFetchedReportName.current) {
      fetchData(effectiveReportName);
    }
  }, [reportName, autoFetch, getEffectiveReportName, fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Determine if any files or links are still actively processing (excludes errored items)
  const hasProcessingItems = useMemo(() => {
    const fileProcessing = data.some(
      (item) => item.Status !== "Analysis complete" && item.Status !== "Error"
    );
    const linkProcessing = linkData.some(
      (link) =>
        link.vector_upload_status !== "completed" &&
        link.vector_upload_status !== "complete" &&
        link.vector_upload_status !== "error" &&
        link.vector_upload_status !== "failed"
    );
    return fileProcessing || linkProcessing;
  }, [data, linkData]);

  // Poll every 10 seconds while items are processing; stop after 30 attempts (~5 min)
  useEffect(() => {
    if (!hasProcessingItems) {
      prevHasProcessingRef.current = false;
      return;
    }
    // Reset attempt counter at the start of each new polling cycle
    if (!prevHasProcessingRef.current) {
      pollAttemptsRef.current = 0;
    }
    prevHasProcessingRef.current = true;
    const pollInterval = setInterval(() => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current >= 30) {
        clearInterval(pollInterval);
        return;
      }
      fetchData();
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [hasProcessingItems, fetchData]);

  return {
    data,
    linkData,
    setLinkData,
    loading,
    error,
    setError,
    refresh,
    fetchData,
    getEffectiveReportName,
    currentReportName: lastFetchedReportName.current,
  };
};

export default useRepositoryData;
